import { faChevronUp, faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { useState, useContext, useEffect, useRef } from 'react';
import { useSelector } from 'react-redux';
import { VIP_IMG } from '../../../config/constants/images';
import {
  FILTER_PRICE_SHORT,
  VIP_DISABLED_POPUP_DESC,
  VIP_DISABLED_POPUP_TITLE,
  VIP_ENABLED_POPUP_DESC,
  VIP_ENABLED_POPUP_TITLE,
  VIP_SEARCH_DISABLED,
  VIP_SEARCH_ENABLED,
} from '../../../config/text/text';
import { isMobileViewport } from '../../../helpers/screen';
import ThemeContext from '../../../providers/ThemeProvider';
import { PriceRangeNewFilter } from '../../filters/RangeSelectorNewFilter';
import CTAButton from '../button/CTAButton';
import Switch from '../switch/Switch';
import useToast from '../toast/UseToast';
import FilterMultiSelectItem from './FilterMultiSelectItem';

const FilterOptions = ({
  categories,
  count,
  vipSearchFilter,
  sortBy,
  sliderValues,
  updateFilter,
  filterData = [],
  priceStats,
  selectedFilters = {},
  setSelectedFilters,
  currentMarks,
  isSubCat,
}) => {
  const theme = useContext(ThemeContext);
  const [showMore, setShowMore] = useState({});
  const [vipFilterTooltip, setVipFilterTooltip] = useState(false);
  const isMobile = isMobileViewport();
  const toast = useToast();
  const isVipUser = useSelector((state) => state?.user?.userprofile?.vipUser);

  const getFirstNonPriceFilterPosition = () => {
    if (!filterData.length) return null;

    const nonPriceFilters = filterData
      .filter(
        (filter) =>
          filter.linkText !== 'Price' &&
          filter.position !== null &&
          Array.isArray(filter.values) &&
          filter.values.length > 0,
      )
      .sort((a, b) => a.position - b.position);

    return nonPriceFilters.length > 0 ? nonPriceFilters[0].position : null;
  };

  const isPriceFilterFirst = () => {
    const firstNonPricePosition = getFirstNonPriceFilterPosition();

    return filterData.some(
      (f) =>
        f.linkText === 'Price' && (f.position ?? 0) < firstNonPricePosition,
    );
  };
  const isFirstRender = useRef(true);
  const isTogglingVipFilter = useRef(false);

  useEffect(() => {
    const initialShowMore = {};
    filterData.forEach((filter) => {
      initialShowMore[filter.linkText] = false;
    });
    setShowMore(initialShowMore);
  }, [filterData]);

  const handleFilterChange = (filterType, value) => {
    setSelectedFilters((previous) => ({
      ...previous,
      [filterType]: value,
    }));
  };
  useEffect(() => {
    if (isFirstRender.current) {
      isFirstRender.current = false;

      return;
    }

    if (isTogglingVipFilter.current) {
      isTogglingVipFilter.current = false;

      return;
    }

    // If VIP search filter is changed, show toast
    if (isVipUser && !isSubCat) {
      toast.addToast(
        vipSearchFilter ? VIP_SEARCH_ENABLED : VIP_SEARCH_DISABLED,
        'toast-success',
      );
    }
  }, [vipSearchFilter]);

  useEffect(() => {
    // Functionality for tooltip to show only for 5 sec for vip search filter
    const showTooltipTimeout = setTimeout(
      () => setVipFilterTooltip(true),
      2_000,
    );
    const hideTooltipTimeout = setTimeout(
      () => setVipFilterTooltip(false),
      17_000,
    );

    return () => {
      clearTimeout(hideTooltipTimeout);
      clearTimeout(showTooltipTimeout);
    };
  }, []);

  const toggleVipFilter = () => {
    const updatedVipFilter = !vipSearchFilter;
    isTogglingVipFilter.current = true;

    updateFilter(
      {},
      true, // preserveExistingFilters
      updatedVipFilter, // Explicit VIP filter value
    );
    setVipFilterTooltip(false);
  };

  const handleShowMore = (filterType) => {
    setShowMore((previous) => ({
      ...previous,
      [filterType]: !previous[filterType],
    }));
  };

  return (
    <div className="filter-options-container">
      {!isMobile && (
        <div className="filters-header">
          <>
            <h2>Filters</h2>
            {count > 0 && (
              <span className="count-results">{`(${count} ${
                count > 1 ? 'Results' : 'Result'
              })`}</span>
            )}
          </>
        </div>
      )}
      <div className="filters-content">
        {isVipUser && !isSubCat && (
          <div className="filter-item--vip-switch">
            <img
              alt="VIP Logo"
              className="filter-item--vip-logo"
              src={VIP_IMG[process.env.NEXT_PUBLIC_BRAND]}
            />
            <Switch
              colorScheme={theme.colors.filtervipswitch}
              isOn={vipSearchFilter}
              label={'Vip Search Switch'}
              onChange={toggleVipFilter}
              size="small"
            />
            <div
              className={classNames('vip-popover', {
                'vip-popover--visible': vipFilterTooltip,
              })}
            >
              <span className="vip-popover-title">
                {vipSearchFilter
                  ? VIP_ENABLED_POPUP_TITLE
                  : VIP_DISABLED_POPUP_TITLE}
              </span>
              <p>
                {vipSearchFilter
                  ? VIP_ENABLED_POPUP_DESC
                  : VIP_DISABLED_POPUP_DESC}
              </p>
              <CTAButton
                className="secondary-button"
                onClick={() => setVipFilterTooltip(false)}
              >
                Got it
              </CTAButton>
            </div>
          </div>
        )}
        {!isSubCat && (
          <div className="price-filter-wrapper">
            <p className="heading">{FILTER_PRICE_SHORT}</p>
            <div className="filter-range-section">
              <PriceRangeNewFilter
                currentMarks={currentMarks}
                inline
                invert={false}
                isNewFilterApplied
                priceStats={priceStats}
                sliderValues={sliderValues}
                sortBy={sortBy}
                subCat={categories}
                updateFilter={updateFilter}
                vipSearchFilter={vipSearchFilter}
              />
            </div>
          </div>
        )}
        {filterData.map((filter) => {
          if (filter.linkText === 'Price' && isSubCat) {
            return (
              <div className="price-filter-wrapper" key="price-filter">
                {filterData?.length !== 1 && !isPriceFilterFirst() && (
                  <div className="line" />
                )}
                <p className="heading">{FILTER_PRICE_SHORT}</p>
                <div className="filter-range-section">
                  <PriceRangeNewFilter
                    currentMarks={currentMarks}
                    inline
                    invert={false}
                    isNewFilterApplied
                    priceStats={priceStats}
                    sliderValues={sliderValues}
                    sortBy={sortBy}
                    subCat={categories}
                    updateFilter={updateFilter}
                    vipSearchFilter={vipSearchFilter}
                  />
                </div>
              </div>
            );
          }

          if (!filter.values) return null;

          return (
            <FilterSectionWithMultiSelect
              firstNonPricePosition={getFirstNonPriceFilterPosition()}
              isPriceFilterFirst={isPriceFilterFirst()}
              isSubCat={isSubCat}
              key={filter.linkText}
              onChange={(value) =>
                handleFilterChange(filter.filterField, value)
              }
              onShowMore={handleShowMore}
              options={filter.values}
              position={filter?.position}
              selectedValues={selectedFilters[filter.filterField] || []}
              showMore={showMore[filter.linkText] || false}
              title={filter.linkText}
              type={filter.linkText}
            />
          );
        })}
      </div>
      <style jsx>{`
        .filter-options-container {
          width: 100%;
          background: white;
          display: flex;
          flex-direction: column;
          height: 100%;
          margin: 0 auto;
          position: relative;
        }
        .filter-item--vip-switch {
          display: flex;
          justify-content: space-between;
          margin-left: -10px;
        }
        .filter-item--vip-logo {
          margin-left: 0px;
          width: 65px;
        }
        .filters-header {
          display: flex;
          justify-content: space-between;
          padding: 10px 10px 10px 15px;
          border-top: 1px solid ${theme.colors.primary};
          border-bottom: 1px solid ${theme.colors.primary};
        }
        .filters-header h2 {
          margin: 0;
          font-size: 20px;
          color: ${theme.colors.primary};
        }
        .heading {
          font-size: 16px;
          font-weight: 600;
          margin: 18px 0;
          text-align: left;
        }
        .filters-content {
          flex: 1;
          overflow-y: auto;
          padding: ${isVipUser && !isSubCat ? '15px' : '0px'} 15px
            ${filterData?.length > 0 ? '0' : '20px'} 20px;
        }
        .price-filter-wrapper {
          margin-bottom: 20px;
        }
        .line {
          border-top: 1px solid ${theme.colors.primary};
        }
        .filter-range-section {
          margin-top: 15px;
          padding: 0 15px 30px 10px;
        }

        .count-results {
          margin-top: 5px;
          color: ${theme.colors.primary};
        }
        .vip-popover {
          visibility: hidden;
          position: absolute;
          display: flex;
          flex-direction: column;
          align-items: center;
          text-align: center;
          padding: 5px;
          color: black;
          width: 220px;
          font-size: 14px;
          z-index: 1000;
          top: 110px;
          left: 45px;
          border: 1px solid ${theme.colors.primary};
          border-radius: 15px;
          background: white;
        }
        .vip-popover:after,
        .vip-popover:before {
          content: ' ';
          position: absolute;
          width: 0;
          height: 0;
          right: 4%;
          top: -39px;
          border: 20px solid transparent;
        }

        .vip-popover:before {
          margin-top: -1.5px;
        }
        .vip-popover:after {
          top: -11px;
          right: 16px;
          height: 20px;
          background: white;
          transform: rotate(45deg);
          border-top: 2px solid ${theme.colors.primary};
          border-left: 2px solid ${theme.colors.primary};
        }

        .vip-popover-title {
          margin-top: 2px;
          font-size: 18px;
          font-weight: 600;
        }

        .vip-popover--visible {
          visibility: visible;
        }
        /* Medium Tablet - mdUp to lgDown */
        @media (min-width: ${theme.breakpoints.mdUp}) and (max-width: ${theme
            .breakpoints.lgDown}) {
          .filter-options-container {
            min-height: 500px;
            border-radius: 12px;
            margin: 0 0;
            padding: 0 20px 20px 20px;
          }
          .count-results {
            margin-top: 5px;
            color: ${theme.colors.primary};
          }
          .filters-header {
            padding: 18px 24px;
            border-radius: 12px 12px 0 0;
          }
          .filters-header h2 {
            font-size: 24px;
            font-weight: 600;
          }
          .heading {
            font-size: 18px;
            margin: 22px 0 14px 0;
          }
          .filters-content {
            padding: 24px;
            padding-bottom: 0px;
          }
          .filter-range-section {
            margin-top: 20px;
            padding: 0 20px 40px 16px;
          }
        }

        /* Small Tablet - smUp to mdDown (576px - 767.98px) */
        @media (min-width: ${theme.breakpoints.smUp}) and (max-width: ${theme
            .breakpoints.mdDown}) {
          .filter-options-container {
            max-width: 380px;
            min-height: 480px;
            border-radius: 10px;
            box-shadow: 0 6px 20px rgba(0, 0, 0, 0.12);
            margin: 0 0;
          }
          .filters-header {
            padding: 16px 20px;
            background: #fafafa;
            border-radius: 10px 10px 0 0;
          }
          .filters-header h2 {
            font-size: 22px;
            font-weight: 600;
          }
          .heading {
            font-size: 17px;
            margin: 20px 0 12px 0;
          }
          .filters-content {
            padding: 20px;
            padding-bottom: 0px;
          }
          .filter-range-section {
            margin-top: 18px;
            padding: 0 18px 35px 14px;
          }
        }
        @media (min-width: ${theme.breakpoints.smDown}) and (max-width: ${theme
            .breakpoints.lgDown}) {
          .filters-header {
            border-top: unset;
            border-bottom: unset;
          }
        }
        /* Mobile - below smUp */
        @media (max-width: ${theme.breakpoints.smDown}) {
          .filter-options-container {
            width: 100%;
            height: max-content;
            border-radius: 0;
            box-shadow: none;
            margin: 0;
            margin-top: 5px;
            border-top: 1px solid ${theme.colors.primary};
          }
          .filters-header {
            padding: 16px 20px;
            background: white;
            border-radius: 0;
          }
          .filters-header h2 {
            font-size: 20px;
            font-weight: 600;
          }
          .heading {
            font-size: 16px;
            margin: 18px 0 10px 0;
          }
          .filters-content {
            padding: 18px;
            padding-bottom: 20px;
            overflow-x: hidden;
          }
          .filter-item--vip-switch {
            margin-left: -4px;
          }
          .filter-range-section {
            margin-top: 16px;
            padding: 0 30px 30px 18px;
          }
          .vip-popover {
            top: 125px;
            left: 45%;
            width: 50%;
          }
          .filter-item--vip-logo {
            margin-left: 2px;
          }
        }
      `}</style>
    </div>
  );
};

const FilterSectionWithMultiSelect = ({
  title,
  options,
  selectedValues,
  onChange,
  showMore,
  onShowMore,
  type,
  isMapPage = false,
  inline = false,
  invert = false,
  position = null,
  isSubCat = false,
  firstNonPricePosition = null,
  isPriceFilterFirst = false,
}) => {
  const hasSelectedValues =
    Array.isArray(selectedValues) && selectedValues.length > 0;
  const [isExpanded, setIsExpanded] = useState(() => {
    return position === firstNonPricePosition || hasSelectedValues;
  });
  const hideBorder = () => {
    if (!isSubCat) return false;

    if (isPriceFilterFirst) return false;

    if (!isPriceFilterFirst && position === firstNonPricePosition) return true;

    return false;
  };
  useEffect(() => {
    if (hasSelectedValues && !isExpanded) {
      setIsExpanded(true);
    }
  }, [hasSelectedValues]);

  const theme = useContext(ThemeContext);

  const displayOptions = showMore ? options : options?.slice(0, 5);
  const hasMore = options?.length > 5;

  const handleItemSelect = (optionName) => {
    // Ensure selectedValues is always treated as an array
    const currentValues = Array.isArray(selectedValues) ? selectedValues : [];

    const newSelected = currentValues.includes(optionName)
      ? currentValues.filter((name) => name !== optionName)
      : [...currentValues, optionName];

    onChange(newSelected);
  };

  return (
    <div className="filter-section">
      {!hideBorder() && <div className="line" />}
      <div className="filter-header" onClick={() => setIsExpanded(!isExpanded)}>
        <h3>{title}</h3>
        <FontAwesomeIcon icon={isExpanded ? faChevronUp : faChevronDown} />
      </div>

      {isExpanded && (
        <div className="filter-options">
          <ul className="filter-options-list">
            {displayOptions?.map((option) => (
              <div className="filter-item-wrapper" key={option.name}>
                <FilterMultiSelectItem
                  inline={inline}
                  invert={invert}
                  isMapPage={isMapPage}
                  isSelected={selectedValues?.includes(option.name)}
                  item={{
                    count: option.count,
                    name: option.name,
                    value: option.name,
                  }}
                  onSelect={handleItemSelect}
                />
                <span className="option-count">({option.count})</span>
              </div>
            ))}
          </ul>

          {hasMore && (
            <button className="show-more-btn" onClick={() => onShowMore(type)}>
              {showMore ? 'Show less' : 'Show more'}
            </button>
          )}
          {!hasMore && <div className="spacer" />}
        </div>
      )}

      <style jsx>{`
        .filter-section {
          padding-bottom: 0;
          margin-bottom: 0;
          position: relative;
        }
        .line {
          border-bottom: 1px solid ${theme.colors.primary};
          margin: 0;
        }
        .filter-header {
          display: flex;
          justify-content: space-between;
          align-items: center;
          cursor: pointer;
          padding: 16px 0;
          transition: all 0.2s ease;
          border-radius: 6px;
        }
        .filter-header h3 {
          margin: 0;
          font-size: 16px;
          font-weight: 600;
          color: #333;
          line-height: 1.2;
          display: flex;
          align-items: center;
          justify-content: center;
        }
        .filter-options {
          padding-left: 0;
        }
        .spacer {
          margin-bottom: 15px;
        }
        .filter-options-list {
          list-style: none;
          padding: 0;
          margin: 0;
        }
        .filter-item-wrapper {
          display: flex;
          align-items: center;
          justify-content: space-between;
          padding: 3px 0;
          border-radius: 4px;
          transition: all 0.2s ease;
        }
        .option-count {
          font-size: 12px;
          color: #888;
          margin-left: 8px;
          font-weight: 500;
        }
        .show-more-btn {
          background: none;
          border: none;
          color: ${theme.colors.primary};
          font-size: 14px;
          cursor: pointer;
          padding: 8px 0;
          text-align: left;
          margin-top: 8px;
          font-weight: 500;
          text-decoration: underline;
          transition: all 0.2s ease;
        }
        @media (min-width: ${theme.breakpoints.mdUp}) and (max-width: ${theme
            .breakpoints.lgDown}) {
          .filter-section {
            padding-bottom: 20px;
            margin-bottom: 10px;
          }
          .filter-header {
            padding: 12px 0;
          }
          .filter-header h3 {
            font-size: 18px;
          }
          .filter-item-wrapper {
            padding: 5px 0;
          }
          .option-count {
            font-size: 14px;
            margin-left: 12px;
          }
          .show-more-btn {
            font-size: 16px;
            padding: 12px 0;
            margin-top: 12px;
          }
        }
        @media (min-width: ${theme.breakpoints.smUp}) and (max-width: ${theme
            .breakpoints.mdDown}) {
          .filter-section {
            padding-bottom: 18px;
            margin-bottom: 8px;
          }
          .filter-header {
            padding: 10px 0;
          }
          .filter-header:hover {
            padding: 10px 10px;
          }
          .filter-header h3 {
            font-size: 17px;
          }
          .filter-item-wrapper {
            padding: 4px 0;
          }
          .option-count {
            font-size: 13px;
            margin-left: 10px;
          }
          .show-more-btn {
            font-size: 15px;
            padding: 10px 0;
            margin-top: 10px;
          }
        }

        /* Mobile - below smUp (up to 575.98px) */
        @media (max-width: ${theme.breakpoints.smDown}) {
          .filter-section {
            padding-bottom: 0px;
            margin-bottom: 0px;
          }
          .filter-header {
            padding: 20px 0;
          }
          .filter-header h3 {
            font-size: 16px;
          }
          .filter-item-wrapper {
            padding: 3px 0;
          }
          .option-count {
            font-size: 12px;
            margin-left: 8px;
          }
          .show-more-btn {
            font-size: 14px;
            padding: 9px 0;
            margin-top: 9px;
          }
        }
      `}</style>
    </div>
  );
};

FilterOptions.propTypes = {
  filterData: PropTypes.arrayOf(
    PropTypes.shape({
      count: PropTypes.number,
      filterField: PropTypes.string,
      linkText: PropTypes.string,
      position: PropTypes.number,
      shortName: PropTypes.string,
      values: PropTypes.arrayOf(
        PropTypes.shape({
          count: PropTypes.number,
          name: PropTypes.string,
        }),
      ),
    }),
  ),
  isSubCat: PropTypes.bool,
  selectedFilters: PropTypes.object,
  setSelectedFilters: PropTypes.func,
};

export default FilterOptions;
